import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './theme-config';
import { ColorSchemeType, DEFAULT_COLOR_SCHEME, getColorSchemeByThemeNumber } from './color-schemes';

interface ThemeContextType {
  colorScheme: ColorSchemeType;
  setColorScheme: (scheme: ColorSchemeType) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
  initialColorScheme?: ColorSchemeType;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialColorScheme = DEFAULT_COLOR_SCHEME
}) => {
  const [colorScheme, setColorScheme] = useState<ColorSchemeType>(initialColorScheme);
  const theme = createAppTheme(colorScheme);

  // Listen for theme changes from parametersPortal
  useEffect(() => {
    const handleThemeUpdate = (event: CustomEvent) => {
      const themeNumber = event.detail?.themeNumber;
      const newColorScheme = getColorSchemeByThemeNumber(themeNumber);
      setColorScheme(newColorScheme);
    };

    window.addEventListener('portalThemeUpdate' as any, handleThemeUpdate);
    return () => {
      window.removeEventListener('portalThemeUpdate' as any, handleThemeUpdate);
    };
  }, []);

  return (
    <ThemeContext.Provider value={{ colorScheme, setColorScheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
